import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  getOverallImpact,
  initiativeErrorMessage,
} from "../services/efficiencyInitiativesApi";

/**
 * Overall Impact — portfolio-wide totals, ROI and the cumulative benefit curve.
 *
 * Two things this panel refuses to do, both deliberate:
 *
 *   * It does not print a zero for CO2 or insight-loss reduction. Neither can be
 *     computed yet, and a zero total reads as "we measured it and it was nothing".
 *   * It does not quietly sum overlapping initiatives. Where one initiative's node
 *     sits inside another's, the same kWh is counted twice; the totals still add
 *     up as the specification asks, but the overlap is shown above them.
 */

const PKR = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 });
const NUM = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 1 });

const fmtPkr = (v) => (v == null ? "—" : `PKR ${PKR.format(v)}`);
const fmtKwh = (v) => (v == null ? "—" : `${PKR.format(v)} kWh`);
const fmtPct = (v) => (v == null ? "—" : `${NUM.format(v)}%`);
const fmtMonths = (v) =>
  v == null ? "No payback" : v < 24 ? `${NUM.format(v)} months` : `${NUM.format(v / 12)} years`;

export default function OverallImpactPanel({ username, statusFilter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOverallImpact(username, statusFilter)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(initiativeErrorMessage(err, "Could not compute overall impact."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [username, statusFilter]);

  // Cumulative benefit against cumulative spend. The crossover is the portfolio's
  // real payback moment, which is why both series share one axis rather than being
  // shown as separate charts.
  const chart = useMemo(() => {
    const rows = data?.cumulative || [];
    if (rows.length === 0) return null;
    const toPoint = (r, field) => ({ x: new Date(r.period).getTime(), y: r[field] });
    return {
      series: [
        { name: "Cumulative saving", data: rows.map((r) => toPoint(r, "cumulative_saving_pkr")) },
        { name: "Cumulative cost", data: rows.map((r) => toPoint(r, "cumulative_cost_pkr")) },
        { name: "Net position", data: rows.map((r) => toPoint(r, "cumulative_net_pkr")) },
      ],
      options: {
        chart: { type: "line", height: 300, toolbar: { show: false }, animations: { enabled: false } },
        colors: ["#1ba08e", "#f97316", "#6366f1"],
        stroke: { width: [3, 2, 2], curve: "smooth", dashArray: [0, 5, 0] },
        dataLabels: { enabled: false },
        markers: { size: 4 },
        xaxis: { type: "datetime", labels: { format: "MMM yyyy", style: { fontSize: "11px" } } },
        yaxis: {
          title: { text: "PKR" },
          labels: { formatter: (v) => PKR.format(v) },
        },
        // Zero matters here: it is the line the net position has to cross.
        annotations: {
          yaxis: [{ y: 0, borderColor: "#94a3b8", strokeDashArray: 3 }],
        },
        legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
        grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
        tooltip: { x: { format: "MMM yyyy" }, y: { formatter: (v) => `PKR ${PKR.format(v)}` } },
      },
    };
  }, [data]);

  if (loading) {
    return (
      <div className="ei-card">
        <p className="ei-hint">Computing measured impact across every initiative…</p>
      </div>
    );
  }
  if (error) return <div className="ei-card"><div className="ei-alert error">{error}</div></div>;
  if (!data) return null;

  const t = data.totals;
  const f = t.financial;
  const dc = data.double_counting;

  return (
    <div className="ei-card">
      <div className="ei-impact-head">
        <h4>
          Overall impact{" "}
          <span className="ei-impact-sub">
            {data.measurable_count} of {data.initiative_count} initiatives measurable ·
            cancelled excluded
          </span>
        </h4>
      </div>

      {dc.at_risk && (
        <div className="ei-alert error">
          <strong>Totals include double counting.</strong>
          <div style={{ marginTop: "0.35rem", fontWeight: 400 }}>
            {dc.note}
            <ul style={{ margin: "0.4rem 0 0 1rem", padding: 0 }}>
              {dc.overlaps.map((o, i) => (
                <li key={i}>
                  <strong>{o.outer}</strong> and <strong>{o.inner}</strong> ({o.relationship}) —{" "}
                  {o.detail}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {data.not_measurable_count > 0 && (
        <div className="ei-alert info">
          {data.not_measurable_count} initiative
          {data.not_measurable_count === 1 ? "" : "s"} could not be measured and contribute
          no saving — but their cost <em>is</em> included, so ROI is not flattered.
        </div>
      )}

      <div className="ei-kpis">
        <Tile label="Total energy saved" value={fmtKwh(t.energy.realised_saving_kwh)}
              sub={`${fmtKwh(t.energy.annualised_saving_kwh)}/yr run-rate`} good />
        <Tile label="Total financial savings" value={fmtPkr(f.realised_saving_pkr)}
              sub={`${fmtPkr(f.annualised_saving_pkr)}/yr run-rate`} good />
        <Tile label="Total implementation cost" value={fmtPkr(f.implementation_cost_pkr)}
              sub="all initiatives, measurable or not" />
        <Tile label="Overall ROI" value={fmtPct(f.overall_roi_percent)}
              sub={`${fmtPct(f.annualised_roi_percent)} annualised`}
              good={f.overall_roi_percent != null && f.overall_roi_percent > 0} />
        <Tile label="Payback period" value={fmtMonths(f.overall_payback_months)}
              sub="portfolio-wide, on the run-rate"
              good={f.overall_payback_months != null} />
        <Tile label="Net position" value={fmtPkr(f.net_position_pkr)}
              sub={f.net_position_pkr >= 0 ? "cost recovered" : "still recovering"}
              good={f.net_position_pkr >= 0} />
        <Tile label="Total peak reduction"
              value={`${NUM.format(t.peak_demand.total_reduction_kw)} kW`}
              sub={`${t.peak_demand.contributing_initiatives} initiatives`}
              good={t.peak_demand.total_reduction_kw > 0} />
        <Tile label="Total CO₂ avoided" value="N/A" sub="no emission factor set" good />
        <Tile label="Total loss reduction" value="N/A" sub="insight comparison not built" good />
      </div>

      <p className="ei-rank-note">
        <strong>ROI basis:</strong> {f.roi_basis}. <strong>Peak demand:</strong>{" "}
        {t.peak_demand.caveat}.
      </p>

      {chart && (
        <>
          <p className="ei-detail-label">Cumulative impact over time</p>
          <div className="ei-impact-chart">
            <ReactApexChart options={chart.options} series={chart.series} type="line" height={300} />
          </div>
          <p className="ei-rank-note">
            Each initiative contributes its measured daily saving for every day since it was
            implemented, and its full cost in the month it went in. Where the net position
            crosses zero is the portfolio's payback point. The daily rate is assumed steady,
            the same assumption the annualised figures make.
          </p>
        </>
      )}

      {data.contributors.length > 0 && (
        <>
          <p className="ei-detail-label">Contribution by initiative</p>
          <div className="ei-table-wrapper">
            <table className="ei-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Initiative</th>
                  <th>Node</th>
                  <th className="ei-num">Energy (kWh/yr)</th>
                  <th className="ei-num">Financial (PKR/yr)</th>
                  <th className="ei-num">Cost (PKR)</th>
                  <th className="ei-num">Share of savings</th>
                </tr>
              </thead>
              <tbody>
                {data.contributors.map((c) => {
                  const share = f.annualised_saving_pkr
                    ? (c.financial.annualised_saving_pkr / f.annualised_saving_pkr) * 100
                    : null;
                  return (
                    <tr key={c.initiative_id} className="ei-row">
                      <td className="ei-ref">{c.reference_code}</td>
                      <td><div className="ei-title-cell">{c.title}</div></td>
                      <td>
                        <span className={`ei-badge node-${c.node_type}`}>{c.node_type}</span>
                        <div className="ei-title-sub">{c.node_name}</div>
                      </td>
                      <td className="ei-num">{PKR.format(c.energy.annualised_saving_kwh)}</td>
                      <td className="ei-num">{PKR.format(c.financial.annualised_saving_pkr)}</td>
                      <td className="ei-num">{PKR.format(c.implementation_cost_pkr || 0)}</td>
                      <td className="ei-num">{share == null ? "—" : `${NUM.format(share)}%`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data.not_measurable.length > 0 && (
        <div style={{ marginTop: "1.25rem" }}>
          <p className="ei-detail-label">Not yet measurable (cost counted, saving not)</p>
          <ul className="ei-rank-list">
            {data.not_measurable.map((n) => (
              <li key={n.initiative_id}>
                <strong>{n.reference_code}</strong> {n.title}
                <div className="ei-title-sub">{n.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, sub, good }) {
  return (
    <div className={`ei-kpi${good ? "" : " neutral"}`}>
      <p className="ei-kpi-label">{label}</p>
      <p className="ei-kpi-value">{value}</p>
      <p className="ei-kpi-sub">{sub}</p>
    </div>
  );
}
